import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RotateCcw, Copy, Download, Store, Truck, Info, AlertTriangle, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// =============================================================================
// TYPES
// =============================================================================

interface SKU {
  id: string;
  nom: string;
  poids: string;
  cogsHT: number;
  coutFixeHT: number;
  pvpMinRecommandeTTC?: number;
  pvpMaxRecommandeTTC?: number;
}

interface Params {
  tva: number;
  margeDetail: number;
  /** Markup distributeur : prix boutique = prix distrib × (1 + margeDistrib) */
  margeDistrib: number;
  tradePct: number;
  agentPct: number;
}

interface ResultRow {
  scenario: 'Direct' | 'Distributeur';
  pvpTTC: number;
  sellInHT: number;
  margeNetteHT: number;
  margePctSellIn: number;
  margePctPVPHT: number;
}

interface BreakdownDirect {
  pvpTTC: number;
  pvpHT: number;
  prixAchatBoutiqueTTC: number;
  sellInMarqueHT: number;
  netFactor: number;
  netSellInHT: number;
  cogsHT: number;
  coutFixeHT: number;
  margeNetteHT: number;
  margePctSellIn: number;
  margePctPVPHT: number;
}

interface BreakdownDistrib {
  pvpTTC: number;
  pvpHT: number;
  prixAchatBoutiqueTTC: number;
  prixDistribBoutiqueTTC: number;
  prixMarqueDistribTTC: number;
  sellInMarqueHT: number;
  netFactor: number;
  netSellInHT: number;
  cogsHT: number;
  coutFixeHT: number;
  margeNetteHT: number;
  margePctSellIn: number;
  margePctPVPHT: number;
}

type MargePctBasis = 'sellIn' | 'pvpHT';

// =============================================================================
// DEFAULT DATA
// =============================================================================

const defaultSKUs: SKU[] = [
  { id: 'tablette-200g', nom: 'Tablette 200g', poids: '200g', cogsHT: 8.25, coutFixeHT: 0 },
  { id: 'barre-80g', nom: 'Barre 80g', poids: '80g', cogsHT: 4.25, coutFixeHT: 0 },
  { id: 'barre-45g', nom: 'Barre 45g', poids: '45g', cogsHT: 2.85, coutFixeHT: 0 },
  { id: 'libre', nom: 'SKU Libre', poids: '-', cogsHT: 0, coutFixeHT: 0 },
];

const defaultParams: Params = {
  tva: 0.20,
  margeDetail: 0.45,
  margeDistrib: 0.20,
  tradePct: 0.00,
  agentPct: 0.00,
};

// =============================================================================
// FORMATTERS & PARSERS
// =============================================================================

const formatEUR = (val: number): string => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
};

const formatPct = (val: number): string => {
  return new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(val);
};

const parseNumber = (val: string): number => {
  const cleaned = val.replace(/[€%\s]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

// =============================================================================
// PURE CALCULATION FUNCTIONS
// =============================================================================

function calcNetFactor(params: Params): number {
  return 1 - params.tradePct - params.agentPct;
}

/**
 * DIRECT FORWARD: PVP TTC donné → calcul sell-in et marge
 */
function computeDirectForward(pvpTTC: number, sku: SKU, params: Params): BreakdownDirect {
  const pvpHT = pvpTTC / (1 + params.tva);
  const prixAchatBoutiqueTTC = pvpTTC * (1 - params.margeDetail);
  const sellInMarqueHT = prixAchatBoutiqueTTC / (1 + params.tva);
  const netFactor = calcNetFactor(params);
  const netSellInHT = sellInMarqueHT * netFactor;
  const margeNetteHT = netSellInHT - sku.cogsHT - sku.coutFixeHT;
  const margePctSellIn = sellInMarqueHT > 0 ? margeNetteHT / sellInMarqueHT : 0;
  const margePctPVPHT = pvpHT > 0 ? margeNetteHT / pvpHT : 0;

  return {
    pvpTTC,
    pvpHT,
    prixAchatBoutiqueTTC,
    sellInMarqueHT,
    netFactor,
    netSellInHT,
    cogsHT: sku.cogsHT,
    coutFixeHT: sku.coutFixeHT,
    margeNetteHT,
    margePctSellIn,
    margePctPVPHT,
  };
}

/**
 * DISTRIBUTEUR FORWARD: PVP TTC donné → calcul sell-in et marge
 * Le distributeur applique un markup: prix boutique = prix distrib × (1 + margeDistrib)
 */
function computeDistribForward(pvpTTC: number, sku: SKU, params: Params): BreakdownDistrib {
  const pvpHT = pvpTTC / (1 + params.tva);
  const prixAchatBoutiqueTTC = pvpTTC * (1 - params.margeDetail);
  const prixDistribBoutiqueTTC = prixAchatBoutiqueTTC; // même valeur, libellé différent
  const prixMarqueDistribTTC = prixAchatBoutiqueTTC / (1 + params.margeDistrib);
  const sellInMarqueHT = prixMarqueDistribTTC / (1 + params.tva);
  const netFactor = calcNetFactor(params);
  const netSellInHT = sellInMarqueHT * netFactor;
  const margeNetteHT = netSellInHT - sku.cogsHT - sku.coutFixeHT;
  const margePctSellIn = sellInMarqueHT > 0 ? margeNetteHT / sellInMarqueHT : 0;
  const margePctPVPHT = pvpHT > 0 ? margeNetteHT / pvpHT : 0;

  return {
    pvpTTC,
    pvpHT,
    prixAchatBoutiqueTTC,
    prixDistribBoutiqueTTC,
    prixMarqueDistribTTC,
    sellInMarqueHT,
    netFactor,
    netSellInHT,
    cogsHT: sku.cogsHT,
    coutFixeHT: sku.coutFixeHT,
    margeNetteHT,
    margePctSellIn,
    margePctPVPHT,
  };
}

/**
 * DIRECT REVERSE: Marge cible donnée → calcul sell-in requis et PVP TTC requis
 */
function computeDirectReverse(margeCibleNetteHT: number, sku: SKU, params: Params): BreakdownDirect {
  const netFactor = calcNetFactor(params);
  const sellInReqHT = (margeCibleNetteHT + sku.cogsHT + sku.coutFixeHT) / netFactor;
  const pvpTTC_req = (sellInReqHT * (1 + params.tva)) / (1 - params.margeDetail);
  const pvpHT = pvpTTC_req / (1 + params.tva);
  const prixAchatBoutiqueTTC = pvpTTC_req * (1 - params.margeDetail);
  const netSellInHT = sellInReqHT * netFactor;
  const margePctSellIn = sellInReqHT > 0 ? margeCibleNetteHT / sellInReqHT : 0;
  const margePctPVPHT = pvpHT > 0 ? margeCibleNetteHT / pvpHT : 0;

  return {
    pvpTTC: pvpTTC_req,
    pvpHT,
    prixAchatBoutiqueTTC,
    sellInMarqueHT: sellInReqHT,
    netFactor,
    netSellInHT,
    cogsHT: sku.cogsHT,
    coutFixeHT: sku.coutFixeHT,
    margeNetteHT: margeCibleNetteHT,
    margePctSellIn,
    margePctPVPHT,
  };
}

/**
 * DISTRIBUTEUR REVERSE: Marge cible donnée → calcul sell-in requis et PVP TTC requis
 */
function computeDistribReverse(margeCibleNetteHT: number, sku: SKU, params: Params): BreakdownDistrib {
  const netFactor = calcNetFactor(params);
  const sellInReqHT = (margeCibleNetteHT + sku.cogsHT + sku.coutFixeHT) / netFactor;
  const pvpTTC_req = (sellInReqHT * (1 + params.tva) * (1 + params.margeDistrib)) / (1 - params.margeDetail);
  const pvpHT = pvpTTC_req / (1 + params.tva);
  const prixAchatBoutiqueTTC = pvpTTC_req * (1 - params.margeDetail);
  const prixDistribBoutiqueTTC = prixAchatBoutiqueTTC;
  const prixMarqueDistribTTC = prixAchatBoutiqueTTC / (1 + params.margeDistrib);
  const netSellInHT = sellInReqHT * netFactor;
  const margePctSellIn = sellInReqHT > 0 ? margeCibleNetteHT / sellInReqHT : 0;
  const margePctPVPHT = pvpHT > 0 ? margeCibleNetteHT / pvpHT : 0;

  return {
    pvpTTC: pvpTTC_req,
    pvpHT,
    prixAchatBoutiqueTTC,
    prixDistribBoutiqueTTC,
    prixMarqueDistribTTC,
    sellInMarqueHT: sellInReqHT,
    netFactor,
    netSellInHT,
    cogsHT: sku.cogsHT,
    coutFixeHT: sku.coutFixeHT,
    margeNetteHT: margeCibleNetteHT,
    margePctSellIn,
    margePctPVPHT,
  };
}

// =============================================================================
// UI COMPONENTS
// =============================================================================

function StatusBadge({ margePct }: { margePct: number }) {
  if (margePct >= 0.35) {
    return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Excellent</Badge>;
  } else if (margePct >= 0.25) {
    return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Acceptable</Badge>;
  } else {
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Critique</Badge>;
  }
}

function CorridorBadge({ pvpTTC, min, max }: { pvpTTC: number; min?: number; max?: number }) {
  if (min !== undefined && min > 0 && pvpTTC < min) {
    return (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Sous-corridor
      </Badge>
    );
  }
  if (max !== undefined && max > 0 && pvpTTC > max) {
    return (
      <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Hors-corridor
      </Badge>
    );
  }
  return null;
}

function BreakdownLine({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex justify-between py-1.5 ${highlight ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function getMargeStatus(margePct: number): { label: string; color: string } {
  if (margePct >= 0.35) return { label: 'excellente', color: 'text-green-400' };
  if (margePct >= 0.25) return { label: 'acceptable', color: 'text-orange-400' };
  return { label: 'critique', color: 'text-red-400' };
}

function DirectBreakdown({ data }: { data: BreakdownDirect }) {
  const status = getMargeStatus(data.margePctSellIn);
  
  return (
    <div className="space-y-4 text-sm">
      {/* Calculs */}
      <div className="space-y-1">
        <BreakdownLine label="PVP TTC" value={formatEUR(data.pvpTTC)} />
        <BreakdownLine label="PVP HT = PVP TTC / (1 + TVA)" value={formatEUR(data.pvpHT)} />
        <Separator className="my-2" />
        <BreakdownLine label="Prix achat boutique TTC = PVP TTC × (1 − marge détail)" value={formatEUR(data.prixAchatBoutiqueTTC)} />
        <BreakdownLine label="Sell-in marque HT = Prix achat boutique TTC / (1 + TVA)" value={formatEUR(data.sellInMarqueHT)} />
        <Separator className="my-2" />
        <BreakdownLine label={`Net factor = 1 − trade − agent = ${formatPct(data.netFactor)}`} value="" />
        <BreakdownLine label="Net sell-in HT = Sell-in marque HT × net factor" value={formatEUR(data.netSellInHT)} />
        <Separator className="my-2" />
        <BreakdownLine label="COGS HT" value={formatEUR(data.cogsHT)} />
        <BreakdownLine label="Coût fixe HT" value={formatEUR(data.coutFixeHT)} />
        <Separator className="my-2" />
        <BreakdownLine label="Marge marque nette HT = Net sell-in − COGS − Coût fixe" value={formatEUR(data.margeNetteHT)} highlight />
        <BreakdownLine label="Marge % (sur sell-in HT)" value={formatPct(data.margePctSellIn)} highlight />
        <BreakdownLine label="Marge % (sur PVP HT)" value={formatPct(data.margePctPVPHT)} />
      </div>

      {/* Interprétation */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
        <p className="font-medium text-primary flex items-center gap-2">
          <Info className="w-4 h-4" />
          Interprétation
        </p>
        <p className="text-muted-foreground leading-relaxed">
          En vente directe à la boutique, vous facturez <strong className="text-foreground">{formatEUR(data.sellInMarqueHT)} HT</strong> par unité. 
          Après déduction des ristournes/commissions ({formatPct(1 - data.netFactor)}), du COGS ({formatEUR(data.cogsHT)}) et des coûts fixes ({formatEUR(data.coutFixeHT)}), 
          votre marge nette est de <strong className="text-foreground">{formatEUR(data.margeNetteHT)}</strong> par unité.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Cette marge représente <strong className={status.color}>{formatPct(data.margePctSellIn)}</strong> de votre prix de vente facturé — 
          une rentabilité <strong className={status.color}>{status.label}</strong>.
          {data.margePctSellIn < 0.25 && (
            <span className="text-red-400"> Envisagez d'augmenter le PVP ou de réduire les coûts pour améliorer la marge.</span>
          )}
          {data.margePctSellIn >= 0.35 && (
            <span className="text-green-400"> Ce niveau de marge offre une bonne capacité d'investissement marketing et de négociation.</span>
          )}
        </p>
      </div>
    </div>
  );
}

function DistribBreakdown({ data }: { data: BreakdownDistrib }) {
  const status = getMargeStatus(data.margePctSellIn);
  const distribTake = data.prixDistribBoutiqueTTC - data.prixMarqueDistribTTC;
  
  return (
    <div className="space-y-4 text-sm">
      {/* Calculs */}
      <div className="space-y-1">
        <BreakdownLine label="PVP TTC" value={formatEUR(data.pvpTTC)} />
        <BreakdownLine label="PVP HT = PVP TTC / (1 + TVA)" value={formatEUR(data.pvpHT)} />
        <Separator className="my-2" />
        <BreakdownLine label="Prix achat boutique TTC = PVP TTC × (1 − marge détail)" value={formatEUR(data.prixAchatBoutiqueTTC)} />
        <BreakdownLine label="Prix distrib → boutique TTC (= prix achat boutique)" value={formatEUR(data.prixDistribBoutiqueTTC)} />
        <BreakdownLine label="Prix marque → distrib TTC = Prix boutique / (1 + markup)" value={formatEUR(data.prixMarqueDistribTTC)} />
        <BreakdownLine label="Sell-in marque HT = Prix marque→distrib TTC / (1 + TVA)" value={formatEUR(data.sellInMarqueHT)} />
        <Separator className="my-2" />
        <BreakdownLine label={`Net factor = 1 − trade − agent = ${formatPct(data.netFactor)}`} value="" />
        <BreakdownLine label="Net sell-in HT = Sell-in marque HT × net factor" value={formatEUR(data.netSellInHT)} />
        <Separator className="my-2" />
        <BreakdownLine label="COGS HT" value={formatEUR(data.cogsHT)} />
        <BreakdownLine label="Coût fixe HT" value={formatEUR(data.coutFixeHT)} />
        <Separator className="my-2" />
        <BreakdownLine label="Marge marque nette HT = Net sell-in − COGS − Coût fixe" value={formatEUR(data.margeNetteHT)} highlight />
        <BreakdownLine label="Marge % (sur sell-in HT)" value={formatPct(data.margePctSellIn)} highlight />
        <BreakdownLine label="Marge % (sur PVP HT)" value={formatPct(data.margePctPVPHT)} />
      </div>

      {/* Interprétation */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
        <p className="font-medium text-primary flex items-center gap-2">
          <Info className="w-4 h-4" />
          Interprétation
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Via distributeur, vous facturez <strong className="text-foreground">{formatEUR(data.sellInMarqueHT)} HT</strong> par unité au distributeur. 
          Celui-ci prélève <strong className="text-foreground">{formatEUR(distribTake)} TTC</strong> de marge pour revendre à la boutique.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Après déduction des ristournes/commissions ({formatPct(1 - data.netFactor)}), du COGS ({formatEUR(data.cogsHT)}) et des coûts fixes ({formatEUR(data.coutFixeHT)}), 
          votre marge nette est de <strong className="text-foreground">{formatEUR(data.margeNetteHT)}</strong> par unité — 
          soit <strong className={status.color}>{formatPct(data.margePctSellIn)}</strong> de votre sell-in.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Cette rentabilité est <strong className={status.color}>{status.label}</strong>.
          {data.margePctSellIn < 0.25 && (
            <span className="text-red-400"> Le passage par un distributeur compresse significativement la marge. Évaluez si le volume additionnel compense cette perte.</span>
          )}
          {data.margePctSellIn >= 0.35 && (
            <span className="text-green-400"> Le circuit distributeur reste rentable, avec une marge permettant de couvrir les aléas et investir.</span>
          )}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SimulateurMargesPhysique() {
  const { toast } = useToast();

  // State
  const [skus, setSkus] = useState<SKU[]>(defaultSKUs);
  const [selectedSkuId, setSelectedSkuId] = useState<string>(defaultSKUs[0].id);
  const [params, setParams] = useState<Params>(defaultParams);
  const [mode, setMode] = useState<'forward' | 'reverse'>('forward');
  const [pvpTTC, setPvpTTC] = useState<string>('29.90');
  const [margeCible, setMargeCible] = useState<string>('5.00');
  const [margePctBasis, setMargePctBasis] = useState<MargePctBasis>('sellIn');

  // Derived
  const selectedSku = useMemo(() => skus.find(s => s.id === selectedSkuId) || skus[0], [skus, selectedSkuId]);

  // Validation
  const validationError = useMemo(() => {
    if (params.tradePct + params.agentPct >= 1) {
      return 'Erreur — La somme Trade + Agent doit être inférieure à 100%';
    }
    if (params.margeDetail >= 1) {
      return 'Erreur — La marge détail doit être inférieure à 100%';
    }
    return null;
  }, [params]);

  // Calculate breakdowns
  const breakdowns = useMemo(() => {
    if (validationError) return null;

    if (mode === 'forward') {
      const pvp = parseNumber(pvpTTC);
      if (pvp <= 0) return null;
      return {
        direct: computeDirectForward(pvp, selectedSku, params),
        distrib: computeDistribForward(pvp, selectedSku, params),
      };
    } else {
      const marge = parseNumber(margeCible);
      if (marge <= 0) return null;
      return {
        direct: computeDirectReverse(marge, selectedSku, params),
        distrib: computeDistribReverse(marge, selectedSku, params),
      };
    }
  }, [mode, pvpTTC, margeCible, selectedSku, params, validationError]);

  // Results for table
  const results: ResultRow[] = useMemo(() => {
    if (!breakdowns) return [];
    return [
      {
        scenario: 'Direct',
        pvpTTC: breakdowns.direct.pvpTTC,
        sellInHT: breakdowns.direct.sellInMarqueHT,
        margeNetteHT: breakdowns.direct.margeNetteHT,
        margePctSellIn: breakdowns.direct.margePctSellIn,
        margePctPVPHT: breakdowns.direct.margePctPVPHT,
      },
      {
        scenario: 'Distributeur',
        pvpTTC: breakdowns.distrib.pvpTTC,
        sellInHT: breakdowns.distrib.sellInMarqueHT,
        margeNetteHT: breakdowns.distrib.margeNetteHT,
        margePctSellIn: breakdowns.distrib.margePctSellIn,
        margePctPVPHT: breakdowns.distrib.margePctPVPHT,
      },
    ];
  }, [breakdowns]);

  // Handlers
  const handleReset = () => {
    setSkus(defaultSKUs);
    setSelectedSkuId(defaultSKUs[0].id);
    setParams(defaultParams);
    setMode('forward');
    setPvpTTC('29.90');
    setMargeCible('5.00');
    setMargePctBasis('sellIn');
    toast({ title: 'Réinitialisé', description: 'Tous les paramètres ont été remis par défaut.' });
  };

  const handleCopyResults = () => {
    const data = {
      sku: selectedSku,
      params,
      mode,
      input: mode === 'forward' ? { pvpTTC: parseNumber(pvpTTC) } : { margeCibleNetteHT: parseNumber(margeCible) },
      results,
      breakdowns,
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast({ title: 'Copié', description: 'Les résultats ont été copiés dans le presse-papiers.' });
  };

  const handleExportCSV = () => {
    if (results.length === 0) return;

    const basisLabel = margePctBasis === 'sellIn' ? 'sur Sell-in HT' : 'sur PVP HT';
    const headers = ['Scénario', 'PVP TTC', 'Sell-in HT', 'Marge nette HT', `Marge % (${basisLabel})`];
    const rows = results.map(r => [
      r.scenario,
      r.pvpTTC.toFixed(2),
      r.sellInHT.toFixed(2),
      r.margeNetteHT.toFixed(2),
      ((margePctBasis === 'sellIn' ? r.margePctSellIn : r.margePctPVPHT) * 100).toFixed(1) + '%',
    ]);

    const csv = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `marges-physique-${selectedSku.nom.replace(/\s/g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Exporté', description: 'Le fichier CSV a été téléchargé.' });
  };

  const handleExportPDF = async () => {
    if (results.length === 0 || !breakdowns) return;

    const dateStr = new Date().toLocaleDateString('fr-FR', { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    });

    const directStatus = breakdowns.direct.margePctSellIn >= 0.35 ? 'Excellent' : 
      breakdowns.direct.margePctSellIn >= 0.25 ? 'Acceptable' : 'Critique';
    const distribStatus = breakdowns.distrib.margePctSellIn >= 0.35 ? 'Excellent' : 
      breakdowns.distrib.margePctSellIn >= 0.25 ? 'Acceptable' : 'Critique';

    const getStatusColor = (status: string) => {
      if (status === 'Excellent') return '#16a34a';
      if (status === 'Acceptable') return '#ea580c';
      return '#dc2626';
    };

    const getStatusBg = (status: string) => {
      if (status === 'Excellent') return '#dcfce7';
      if (status === 'Acceptable') return '#ffedd5';
      return '#fee2e2';
    };

    // Generate interpretation texts
    const directInterpretation = breakdowns.direct.margePctSellIn >= 0.35 
      ? `Excellente rentabilité avec ${formatPct(breakdowns.direct.margePctSellIn)} de marge sur le sell-in. Cette marge offre une bonne capacité d'investissement marketing et de négociation.`
      : breakdowns.direct.margePctSellIn >= 0.25
        ? `Rentabilité acceptable avec ${formatPct(breakdowns.direct.margePctSellIn)} de marge sur le sell-in. La marge couvre les coûts mais reste limitée pour les investissements.`
        : `Marge critique de ${formatPct(breakdowns.direct.margePctSellIn)} sur le sell-in. Envisagez d'augmenter le PVP ou de réduire les coûts pour améliorer la rentabilité.`;

    const distribTake = breakdowns.distrib.prixDistribBoutiqueTTC - breakdowns.distrib.prixMarqueDistribTTC;
    const distribInterpretation = breakdowns.distrib.margePctSellIn >= 0.35 
      ? `Le circuit distributeur reste rentable avec ${formatPct(breakdowns.distrib.margePctSellIn)} de marge. Le distributeur prélève ${formatEUR(distribTake)} TTC par unité.`
      : breakdowns.distrib.margePctSellIn >= 0.25
        ? `Rentabilité acceptable via distributeur (${formatPct(breakdowns.distrib.margePctSellIn)}). Le distributeur prélève ${formatEUR(distribTake)} TTC, comprimant la marge.`
        : `Marge critique de ${formatPct(breakdowns.distrib.margePctSellIn)} via distributeur. Le markup distributeur (${formatEUR(distribTake)} TTC) compresse significativement la rentabilité.`;

    const deltaMarge = breakdowns.direct.margeNetteHT - breakdowns.distrib.margeNetteHT;
    const deltaPct = breakdowns.direct.margePctSellIn - breakdowns.distrib.margePctSellIn;

    const htmlContent = `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; padding: 32px 40px; background: white; width: 100%; box-sizing: border-box;">
        
        <!-- Header with logo placeholder -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 3px solid #6b7142;">
          <div>
            <h1 style="font-size: 24px; margin: 0 0 4px 0; color: #1a1a1a; font-weight: 600;">
              Analyse des Marges
            </h1>
            <p style="color: #666; margin: 0; font-size: 13px;">
              Circuit Physique — ${selectedSku.nom} (${selectedSku.poids})
            </p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; font-size: 18px; color: #1a1a1a;">
              <strong style="font-weight: 700;">Cosmos Wave</strong> <span style="font-weight: 400; color: #666;">Marketing</span>
            </p>
            <p style="margin: 4px 0 0 0; font-size: 11px; color: #999;">${dateStr}</p>
          </div>
        </div>

        <!-- Executive Summary -->
        <div style="background: linear-gradient(135deg, #6b7142 0%, #8a9155 100%); color: white; padding: 24px; border-radius: 12px; margin-bottom: 28px;">
          <h2 style="margin: 0 0 16px 0; font-size: 16px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">
            Récapitulatif Exécutif
          </h2>
          <div style="display: flex; gap: 32px;">
            <div style="flex: 1; background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px;">
              <p style="margin: 0 0 4px 0; font-size: 11px; text-transform: uppercase; opacity: 0.8;">Scénario Direct</p>
              <p style="margin: 0; font-size: 28px; font-weight: 700;">${formatEUR(breakdowns.direct.margeNetteHT)}</p>
              <p style="margin: 4px 0 0 0; font-size: 13px;">soit ${formatPct(breakdowns.direct.margePctSellIn)} du sell-in</p>
            </div>
            <div style="flex: 1; background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px;">
              <p style="margin: 0 0 4px 0; font-size: 11px; text-transform: uppercase; opacity: 0.8;">Scénario Distributeur</p>
              <p style="margin: 0; font-size: 28px; font-weight: 700;">${formatEUR(breakdowns.distrib.margeNetteHT)}</p>
              <p style="margin: 4px 0 0 0; font-size: 13px;">soit ${formatPct(breakdowns.distrib.margePctSellIn)} du sell-in</p>
            </div>
            <div style="flex: 1; background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px;">
              <p style="margin: 0 0 4px 0; font-size: 11px; text-transform: uppercase; opacity: 0.8;">Écart Direct vs Distrib.</p>
              <p style="margin: 0; font-size: 28px; font-weight: 700;">${deltaMarge >= 0 ? '+' : ''}${formatEUR(deltaMarge)}</p>
              <p style="margin: 4px 0 0 0; font-size: 13px;">${deltaPct >= 0 ? '+' : ''}${formatPct(deltaPct)} de marge</p>
            </div>
          </div>
        </div>

        <!-- Params Grid -->
        <div style="display: flex; gap: 20px; margin-bottom: 28px;">
          <div style="flex: 1; background: #f9fafb; padding: 18px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="margin: 0 0 12px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7142; font-weight: 600;">Produit</h3>
            <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
              <tr><td style="padding: 4px 0; color: #666;">COGS HT</td><td style="text-align: right; font-weight: 500;">${formatEUR(selectedSku.cogsHT)}</td></tr>
              <tr><td style="padding: 4px 0; color: #666;">Coût fixe HT</td><td style="text-align: right; font-weight: 500;">${formatEUR(selectedSku.coutFixeHT)}</td></tr>
              <tr><td style="padding: 4px 0; color: #666;">PVP TTC analysé</td><td style="text-align: right; font-weight: 600; color: #6b7142;">${formatEUR(breakdowns.direct.pvpTTC)}</td></tr>
            </table>
          </div>
          <div style="flex: 1; background: #f9fafb; padding: 18px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="margin: 0 0 12px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7142; font-weight: 600;">Réseau</h3>
            <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
              <tr><td style="padding: 4px 0; color: #666;">TVA</td><td style="text-align: right; font-weight: 500;">${formatPct(params.tva)}</td></tr>
              <tr><td style="padding: 4px 0; color: #666;">Marge détail</td><td style="text-align: right; font-weight: 500;">${formatPct(params.margeDetail)}</td></tr>
              <tr><td style="padding: 4px 0; color: #666;">Markup distributeur</td><td style="text-align: right; font-weight: 500;">${formatPct(params.margeDistrib)}</td></tr>
            </table>
          </div>
          <div style="flex: 1; background: #f9fafb; padding: 18px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="margin: 0 0 12px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7142; font-weight: 600;">Déductions</h3>
            <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
              <tr><td style="padding: 4px 0; color: #666;">Trade / Ristournes</td><td style="text-align: right; font-weight: 500;">${formatPct(params.tradePct)}</td></tr>
              <tr><td style="padding: 4px 0; color: #666;">Commission agent</td><td style="text-align: right; font-weight: 500;">${formatPct(params.agentPct)}</td></tr>
              <tr><td style="padding: 4px 0; color: #666;">Net factor</td><td style="text-align: right; font-weight: 600; color: #6b7142;">${formatPct(breakdowns.direct.netFactor)}</td></tr>
            </table>
          </div>
        </div>

        <!-- Results Table -->
        <h2 style="font-size: 14px; margin: 0 0 12px 0; color: #1a1a1a; text-transform: uppercase; letter-spacing: 0.5px;">Comparatif des scénarios</h2>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 12px;">
          <thead>
            <tr style="background: #374151; color: white;">
              <th style="padding: 10px 12px; text-align: left; font-weight: 500;">Scénario</th>
              <th style="padding: 10px 12px; text-align: right; font-weight: 500;">PVP TTC</th>
              <th style="padding: 10px 12px; text-align: right; font-weight: 500;">Sell-in HT</th>
              <th style="padding: 10px 12px; text-align: right; font-weight: 500;">Marge nette HT</th>
              <th style="padding: 10px 12px; text-align: right; font-weight: 500;">Marge %</th>
              <th style="padding: 10px 12px; text-align: center; font-weight: 500;">Statut</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background: #f9fafb;">
              <td style="padding: 10px 12px; font-weight: 600; border-bottom: 1px solid #e5e7eb;">Direct (Marque → Boutique)</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; border-bottom: 1px solid #e5e7eb;">${formatEUR(breakdowns.direct.pvpTTC)}</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; color: #6b7142; font-weight: 600; border-bottom: 1px solid #e5e7eb;">${formatEUR(breakdowns.direct.sellInMarqueHT)}</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; font-weight: 700; font-size: 14px; border-bottom: 1px solid #e5e7eb;">${formatEUR(breakdowns.direct.margeNetteHT)}</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; font-weight: 600; border-bottom: 1px solid #e5e7eb;">${formatPct(breakdowns.direct.margePctSellIn)}</td>
              <td style="padding: 10px 12px; text-align: center; border-bottom: 1px solid #e5e7eb;"><span style="background: ${getStatusBg(directStatus)}; color: ${getStatusColor(directStatus)}; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600;">${directStatus}</span></td>
            </tr>
            <tr>
              <td style="padding: 10px 12px; font-weight: 600; border-bottom: 1px solid #e5e7eb;">Distributeur (Marque → Distrib. → Boutique)</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; border-bottom: 1px solid #e5e7eb;">${formatEUR(breakdowns.distrib.pvpTTC)}</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; color: #6b7142; font-weight: 600; border-bottom: 1px solid #e5e7eb;">${formatEUR(breakdowns.distrib.sellInMarqueHT)}</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; font-weight: 700; font-size: 14px; border-bottom: 1px solid #e5e7eb;">${formatEUR(breakdowns.distrib.margeNetteHT)}</td>
              <td style="padding: 10px 12px; text-align: right; font-family: monospace; font-weight: 600; border-bottom: 1px solid #e5e7eb;">${formatPct(breakdowns.distrib.margePctSellIn)}</td>
              <td style="padding: 10px 12px; text-align: center; border-bottom: 1px solid #e5e7eb;"><span style="background: ${getStatusBg(distribStatus)}; color: ${getStatusColor(distribStatus)}; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600;">${distribStatus}</span></td>
            </tr>
          </tbody>
        </table>

        <!-- Interpretations -->
        <h2 style="font-size: 14px; margin: 0 0 12px 0; color: #1a1a1a; text-transform: uppercase; letter-spacing: 0.5px;">Analyse et Recommandations</h2>
        
        <div style="background: ${getStatusBg(directStatus)}; border-left: 4px solid ${getStatusColor(directStatus)}; padding: 16px 20px; border-radius: 0 8px 8px 0; margin-bottom: 12px;">
          <h4 style="margin: 0 0 8px 0; font-size: 13px; color: ${getStatusColor(directStatus)}; font-weight: 600;">
            Scénario Direct — ${directStatus}
          </h4>
          <p style="margin: 0; font-size: 12px; color: #374151; line-height: 1.6;">
            ${directInterpretation}
            <br/><strong>Sell-in facturé : ${formatEUR(breakdowns.direct.sellInMarqueHT)} HT</strong> — après déductions : ${formatEUR(breakdowns.direct.netSellInHT)} HT net.
          </p>
        </div>

        <div style="background: ${getStatusBg(distribStatus)}; border-left: 4px solid ${getStatusColor(distribStatus)}; padding: 16px 20px; border-radius: 0 8px 8px 0; margin-bottom: 24px;">
          <h4 style="margin: 0 0 8px 0; font-size: 13px; color: ${getStatusColor(distribStatus)}; font-weight: 600;">
            Scénario Distributeur — ${distribStatus}
          </h4>
          <p style="margin: 0; font-size: 12px; color: #374151; line-height: 1.6;">
            ${distribInterpretation}
            <br/><strong>Sell-in facturé : ${formatEUR(breakdowns.distrib.sellInMarqueHT)} HT</strong> — après déductions : ${formatEUR(breakdowns.distrib.netSellInHT)} HT net.
          </p>
        </div>

        <!-- Breakdown Details -->
        <div style="display: flex; gap: 16px; margin-bottom: 24px;">
          <div style="flex: 1; background: #f9fafb; padding: 16px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h4 style="margin: 0 0 10px 0; font-size: 11px; text-transform: uppercase; color: #6b7142; font-weight: 600;">Décomposition Direct</h4>
            <table style="width: 100%; font-size: 11px; border-collapse: collapse;">
              <tr><td style="padding: 3px 0; color: #666;">PVP TTC</td><td style="text-align: right; font-family: monospace;">${formatEUR(breakdowns.direct.pvpTTC)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">PVP HT</td><td style="text-align: right; font-family: monospace;">${formatEUR(breakdowns.direct.pvpHT)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">Prix achat boutique TTC</td><td style="text-align: right; font-family: monospace;">${formatEUR(breakdowns.direct.prixAchatBoutiqueTTC)}</td></tr>
              <tr><td style="padding: 3px 0; color: #6b7142; font-weight: 500;">Sell-in marque HT</td><td style="text-align: right; font-family: monospace; color: #6b7142; font-weight: 600;">${formatEUR(breakdowns.direct.sellInMarqueHT)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">− COGS HT</td><td style="text-align: right; font-family: monospace; color: #dc2626;">−${formatEUR(breakdowns.direct.cogsHT)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">− Coût fixe HT</td><td style="text-align: right; font-family: monospace; color: #dc2626;">−${formatEUR(breakdowns.direct.coutFixeHT)}</td></tr>
              <tr style="border-top: 1px solid #d1d5db;"><td style="padding: 6px 0 3px 0; font-weight: 700;">= Marge nette HT</td><td style="text-align: right; font-family: monospace; font-weight: 700; font-size: 13px;">${formatEUR(breakdowns.direct.margeNetteHT)}</td></tr>
            </table>
          </div>
          <div style="flex: 1; background: #f9fafb; padding: 16px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h4 style="margin: 0 0 10px 0; font-size: 11px; text-transform: uppercase; color: #666; font-weight: 600;">Décomposition Distributeur</h4>
            <table style="width: 100%; font-size: 11px; border-collapse: collapse;">
              <tr><td style="padding: 3px 0; color: #666;">PVP TTC</td><td style="text-align: right; font-family: monospace;">${formatEUR(breakdowns.distrib.pvpTTC)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">Prix distrib → boutique TTC</td><td style="text-align: right; font-family: monospace;">${formatEUR(breakdowns.distrib.prixDistribBoutiqueTTC)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">Prix marque → distrib TTC</td><td style="text-align: right; font-family: monospace;">${formatEUR(breakdowns.distrib.prixMarqueDistribTTC)}</td></tr>
              <tr><td style="padding: 3px 0; color: #6b7142; font-weight: 500;">Sell-in marque HT</td><td style="text-align: right; font-family: monospace; color: #6b7142; font-weight: 600;">${formatEUR(breakdowns.distrib.sellInMarqueHT)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">− COGS HT</td><td style="text-align: right; font-family: monospace; color: #dc2626;">−${formatEUR(breakdowns.distrib.cogsHT)}</td></tr>
              <tr><td style="padding: 3px 0; color: #666;">− Coût fixe HT</td><td style="text-align: right; font-family: monospace; color: #dc2626;">−${formatEUR(breakdowns.distrib.coutFixeHT)}</td></tr>
              <tr style="border-top: 1px solid #d1d5db;"><td style="padding: 6px 0 3px 0; font-weight: 700;">= Marge nette HT</td><td style="text-align: right; font-family: monospace; font-weight: 700; font-size: 13px;">${formatEUR(breakdowns.distrib.margeNetteHT)}</td></tr>
            </table>
          </div>
        </div>

        <!-- Footer Note -->
        <p style="font-size: 10px; color: #9ca3af; margin: 0 0 20px 0; padding: 10px; background: #f9fafb; border-radius: 4px; border: 1px solid #e5e7eb;">
          <strong>Note :</strong> Marge marque = marge nette HT avant TVA, avant coûts non modélisés (transport, casse, retours), sauf si inclus dans COGS. 
          Mode de calcul : ${mode === 'forward' ? 'Forward (PVP → Marge)' : 'Reverse (Marge → PVP)'}.
        </p>

        <!-- Signature -->
        <div style="text-align: center; padding-top: 16px; border-top: 2px solid #6b7142;">
          <p style="margin: 0 0 2px 0; font-size: 14px; color: #1a1a1a;">
            <strong style="font-weight: 700;">Cosmos Wave</strong> <span style="font-weight: 400; color: #666;">Marketing</span>
          </p>
          <p style="margin: 0; font-size: 11px; color: #6b7142;">www.cosmos-wave.com</p>
        </div>
      </div>
    `;

    // Create temporary container - use fixed positioning with visibility hidden
    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '210mm';
    container.style.background = 'white';
    container.style.zIndex = '-9999';
    container.style.visibility = 'hidden';
    document.body.appendChild(container);

    // Wait for DOM to render
    await new Promise(resolve => setTimeout(resolve, 150));

    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const pdfElement = container.firstElementChild as HTMLElement;
      
      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename: `analyse-marges-${selectedSku.nom.replace(/\s/g, '-')}-${dateStr.replace(/\//g, '-')}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2, 
            useCORS: true, 
            backgroundColor: '#ffffff', 
            logging: false,
            allowTaint: true,
            windowWidth: 794
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(pdfElement)
        .save();
      
      toast({ title: 'PDF généré', description: 'Le rapport a été téléchargé.' });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({ title: 'Erreur', description: 'Impossible de générer le PDF.', variant: 'destructive' });
    } finally {
      document.body.removeChild(container);
    }
  };

  const updateSkuField = <K extends keyof SKU>(field: K, value: SKU[K]) => {
    setSkus(prev =>
      prev.map(s => (s.id === selectedSkuId ? { ...s, [field]: value } : s))
    );
  };

  const updateParam = (field: keyof Params, value: number) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const getMargePct = (row: ResultRow) => (margePctBasis === 'sellIn' ? row.margePctSellIn : row.margePctPVPHT);

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Simulateur Marges — Circuit Physique</h1>
          <p className="text-muted-foreground">Simuler PVP ↔ marge marque, Direct vs Distributeur</p>
        </div>

        {/* Validation Error */}
        {validationError && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="py-4">
              <p className="text-destructive font-medium">{validationError}</p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Bloc Inputs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Paramètres Produit</CardTitle>
              <CardDescription>Sélectionnez un SKU et ajustez les coûts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>SKU</Label>
                <Select value={selectedSkuId} onValueChange={setSelectedSkuId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {skus.map(sku => (
                      <SelectItem key={sku.id} value={sku.id}>
                        {sku.nom} {sku.poids !== '-' && `(${sku.poids})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Champs personnalisés pour SKU Libre */}
              {selectedSkuId === 'libre' && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="space-y-2">
                    <Label className="text-xs text-primary">Nom du produit</Label>
                    <Input
                      type="text"
                      placeholder="Ex: Mon produit"
                      value={selectedSku.nom === 'SKU Libre' ? '' : selectedSku.nom}
                      onChange={e => updateSkuField('nom', e.target.value || 'SKU Libre')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-primary">Poids/Format</Label>
                    <Input
                      type="text"
                      placeholder="Ex: 100g"
                      value={selectedSku.poids === '-' ? '' : selectedSku.poids}
                      onChange={e => updateSkuField('poids', e.target.value || '-')}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>COGS HT (€)</Label>
                  <Input
                    type="text"
                    value={selectedSku.cogsHT}
                    onChange={e => updateSkuField('cogsHT', parseNumber(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Coût fixe HT (€)</Label>
                  <Input
                    type="text"
                    value={selectedSku.coutFixeHT}
                    onChange={e => updateSkuField('coutFixeHT', parseNumber(e.target.value))}
                  />
                </div>
              </div>

              {/* Price Corridor */}
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Price corridor (optionnel)
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">PVP min TTC (€)</Label>
                    <Input
                      type="text"
                      placeholder="—"
                      value={selectedSku.pvpMinRecommandeTTC ?? ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateSkuField('pvpMinRecommandeTTC', val ? parseNumber(val) : undefined);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">PVP max TTC (€)</Label>
                    <Input
                      type="text"
                      placeholder="—"
                      value={selectedSku.pvpMaxRecommandeTTC ?? ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateSkuField('pvpMaxRecommandeTTC', val ? parseNumber(val) : undefined);
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bloc Paramètres Réseau */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Paramètres Réseau</CardTitle>
              <CardDescription>Marges et commissions du circuit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>TVA (%)</Label>
                  <Input
                    type="text"
                    value={(params.tva * 100).toFixed(0)}
                    onChange={e => updateParam('tva', parseNumber(e.target.value) / 100)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Marge détail (%)</Label>
                  <Input
                    type="text"
                    value={(params.margeDetail * 100).toFixed(0)}
                    onChange={e => updateParam('margeDetail', parseNumber(e.target.value) / 100)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Markup distributeur (%)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>
                        Le distributeur revend à la boutique avec +X% au-dessus de son prix d'achat marque.
                        <br />
                        <span className="text-muted-foreground">
                          Ex: +20% = prix boutique = prix distrib × 1,20
                        </span>
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  type="text"
                  value={(params.margeDistrib * 100).toFixed(0)}
                  onChange={e => updateParam('margeDistrib', parseNumber(e.target.value) / 100)}
                />
                <p className="text-xs text-muted-foreground">
                  Le distributeur revend à la boutique avec +X% au-dessus de son prix d'achat marque.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Trade / Ristournes (%)</Label>
                  <Input
                    type="text"
                    value={(params.tradePct * 100).toFixed(0)}
                    onChange={e => updateParam('tradePct', parseNumber(e.target.value) / 100)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Commission agent (%)</Label>
                  <Input
                    type="text"
                    value={(params.agentPct * 100).toFixed(0)}
                    onChange={e => updateParam('agentPct', parseNumber(e.target.value) / 100)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bloc Mode */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Mode de calcul</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={mode} onValueChange={v => setMode(v as 'forward' | 'reverse')}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="forward">Forward — PVP → Marge</TabsTrigger>
                <TabsTrigger value="reverse">Reverse — Marge → PVP</TabsTrigger>
              </TabsList>

              <TabsContent value="forward" className="space-y-4">
                <div className="space-y-2">
                  <Label>Prix de vente public TTC (€)</Label>
                  <Input
                    type="text"
                    value={pvpTTC}
                    onChange={e => setPvpTTC(e.target.value)}
                    placeholder="29.90"
                    className="text-lg font-medium"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Saisissez le PVP TTC pour calculer le sell-in et la marge marque.
                </p>
              </TabsContent>

              <TabsContent value="reverse" className="space-y-4">
                <div className="space-y-2">
                  <Label>Marge marque nette cible HT (€)</Label>
                  <Input
                    type="text"
                    value={margeCible}
                    onChange={e => setMargeCible(e.target.value)}
                    placeholder="5.00"
                    className="text-lg font-medium"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Saisissez la marge cible pour calculer le sell-in et le PVP TTC requis.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Bloc Résultats */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">Résultats comparatifs</CardTitle>
                <CardDescription>Comparaison Direct vs avec Distributeur</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Marge % calculée sur</span>
                <ToggleGroup
                  type="single"
                  value={margePctBasis}
                  onValueChange={v => v && setMargePctBasis(v as MargePctBasis)}
                  className="bg-muted/50 rounded-lg p-0.5"
                >
                  <ToggleGroupItem value="sellIn" className="text-xs px-3 py-1 h-auto data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                    Sell-in HT
                  </ToggleGroupItem>
                  <ToggleGroupItem value="pvpHT" className="text-xs px-3 py-1 h-auto data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                    PVP HT
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {results.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-3 px-4 text-left font-medium text-muted-foreground">Scénario</th>
                        <th className="py-3 px-4 text-right font-medium text-muted-foreground">PVP TTC</th>
                        <th className="py-3 px-4 text-right font-medium text-muted-foreground">Sell-in HT (prix facturé)</th>
                        <th className="py-3 px-4 text-right font-medium text-muted-foreground">Marge nette HT</th>
                        <th className="py-3 px-4 text-right font-medium text-muted-foreground">Marge %</th>
                        <th className="py-3 px-4 text-center font-medium text-muted-foreground">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((row, idx) => (
                        <tr key={row.scenario} className={idx === 0 ? 'bg-primary/5' : ''}>
                          <td className="py-3 px-4 font-medium flex items-center gap-2">
                            {row.scenario === 'Direct' ? (
                              <Store className="w-4 h-4 text-primary" />
                            ) : (
                              <Truck className="w-4 h-4 text-muted-foreground" />
                            )}
                            {row.scenario}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex flex-col items-end gap-1">
                              <span className="font-mono">
                                {mode === 'forward' && row.scenario === 'Direct' ? (
                                  <span className="text-muted-foreground">{formatEUR(row.pvpTTC)}</span>
                                ) : (
                                  formatEUR(row.pvpTTC)
                                )}
                              </span>
                              <CorridorBadge
                                pvpTTC={row.pvpTTC}
                                min={selectedSku.pvpMinRecommandeTTC}
                                max={selectedSku.pvpMaxRecommandeTTC}
                              />
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-primary/80">{formatEUR(row.sellInHT)}</td>
                          <td className="py-3 px-4 text-right font-mono font-bold">{formatEUR(row.margeNetteHT)}</td>
                          <td className="py-3 px-4 text-right font-mono">{formatPct(getMargePct(row))}</td>
                          <td className="py-3 px-4 text-center">
                            <StatusBadge margePct={getMargePct(row)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Breakdown Accordions */}
                {breakdowns && (
                  <Accordion type="multiple" className="w-full">
                    <AccordionItem value="direct">
                      <AccordionTrigger className="text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4 text-primary" />
                          Breakdown — Direct
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <DirectBreakdown data={breakdowns.direct} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="distrib">
                      <AccordionTrigger className="text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-muted-foreground" />
                          Breakdown — Distributeur
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <DistribBreakdown data={breakdowns.distrib} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}

                {/* Note */}
                <p className="text-xs text-muted-foreground border-t border-border pt-4">
                  Marge marque = marge nette HT avant TVA, avant coûts non modélisés (transport, casse, retours), sauf si
                  inclus dans COGS.
                </p>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {validationError ? 'Corrigez les erreurs pour voir les résultats' : 'Saisissez les données pour voir les résultats'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 justify-center">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
          <Button variant="outline" onClick={handleCopyResults} disabled={results.length === 0}>
            <Copy className="w-4 h-4 mr-2" />
            Copier résultat
          </Button>
          <Button variant="outline" onClick={handleExportCSV} disabled={results.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={results.length === 0}>
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
}
